import type { Topic } from './types';
import type { TopicMutation } from './mutations';

export interface TopicState {
  byId: Record<string, Topic>;
  allIds: string[];
  loading: boolean;
  // Per-topic append-only log of the field mutations the user committed during
  // this session, keyed by topic id. In-memory only (cleared on reload), it
  // powers the change-history subpage (/topics/{id}/changes) (#418).
  mutationsById: Record<string, TopicMutation[]>;
}

export type TopicReducerTopic =
  | { type: 'SET_TOPICS'; topics: Topic[] }
  | { type: 'UPSERT_TOPIC'; topic: Topic }
  | { type: 'REMOVE_TOPIC'; topicId: string }
  | { type: 'SET_LOADING'; loading: boolean }
  | {
      type: 'UPDATE_CUSTOM_TEMPLATES';
      topicId: string;
      customTemplates?: string;
    }
  // Append a batch of field mutations to an topic's change log, or clear it (#418).
  | {
      type: 'LOG_TOPIC_MUTATIONS';
      topicId: string;
      mutations: TopicMutation[];
    }
  | { type: 'CLEAR_TOPIC_MUTATIONS'; topicId: string }
  // Swap a manager's email everywhere it appears in any loaded topic's
  // managerEmails, used when the current user verifies a new email so the
  // "listing managers" details reflect the change without a refetch (#288).
  | { type: 'REPLACE_MANAGER_EMAIL'; oldEmail: string; newEmail: string };

export function createEmptyState(): TopicState {
  return { byId: {}, allIds: [], loading: false, mutationsById: {} };
}

export function topicReducer(
  state: TopicState,
  topic: TopicReducerTopic
): TopicState {
  switch (topic.type) {
    case 'SET_TOPICS': {
      const byId: Record<string, Topic> = {};
      const allIds: string[] = [];
      for (const a of topic.topics) {
        // Merge into any existing entry so detail-only fields (e.g.
        // introCount, which the list endpoint doesn't return) are preserved
        // when the list fetch races against a per-topic detail fetch.
        const existing = state.byId[a.id];
        const merged = { ...existing, ...a };
        // The list is fetched with thumbnailImageOnly + skipCustomTemplates, so it
        // carries no imageData or customTemplates. The list is never
        // authoritative for those full values, so their absence must not clobber what a detail
        // fetch (UPSERT_TOPIC) already loaded — otherwise a background list refresh
        // wipes the full-size image out of the open detail view.
        if (a.imageData == null && existing?.imageData != null) {
          merged.imageData = existing.imageData;
        }
        if (a.customTemplates == null && existing?.customTemplates != null) {
          merged.customTemplates = existing.customTemplates;
        }
        byId[a.id] = merged;
        allIds.push(a.id);
      }
      return { ...state, byId, allIds };
    }
    case 'UPSERT_TOPIC': {
      if (state.byId[topic.topic.id]) {
        return {
          ...state,
          byId: { ...state.byId, [topic.topic.id]: topic.topic },
        };
      }
      return {
        ...state,
        byId: { ...state.byId, [topic.topic.id]: topic.topic },
        allIds: [...state.allIds, topic.topic.id],
      };
    }
    case 'REMOVE_TOPIC': {
      if (!state.byId[topic.topicId]) {
        return state;
      }
      const next = { ...state.byId };
      delete next[topic.topicId];
      const nextMutations = { ...state.mutationsById };
      delete nextMutations[topic.topicId];
      return {
        ...state,
        byId: next,
        allIds: state.allIds.filter(id => id !== topic.topicId),
        mutationsById: nextMutations,
      };
    }
    case 'SET_LOADING': {
      return { ...state, loading: topic.loading };
    }
    case 'LOG_TOPIC_MUTATIONS': {
      if (topic.mutations.length === 0) {
        return state;
      }
      const existing = state.mutationsById[topic.topicId] ?? [];
      // De-dupe ids so React keys / open-state stay unique even if the same
      // field is committed twice under an identical `at` timestamp.
      const seen = new Set(existing.map(m => m.id));
      const appended = topic.mutations.map(m => {
        let id = m.id;
        let n = 1;
        while (seen.has(id)) {
          id = `${m.id}#${n++}`;
        }
        seen.add(id);
        return id === m.id ? m : { ...m, id };
      });
      return {
        ...state,
        mutationsById: {
          ...state.mutationsById,
          [topic.topicId]: [...existing, ...appended],
        },
      };
    }
    case 'CLEAR_TOPIC_MUTATIONS': {
      if (!state.mutationsById[topic.topicId]) {
        return state;
      }
      const nextMutations = { ...state.mutationsById };
      delete nextMutations[topic.topicId];
      return { ...state, mutationsById: nextMutations };
    }
    case 'REPLACE_MANAGER_EMAIL': {
      const oldEmail = topic.oldEmail.trim().toLowerCase();
      const newEmail = topic.newEmail.trim();
      // Nothing to do for an empty or no-op (same address) swap.
      if (
        oldEmail === '' ||
        newEmail === '' ||
        oldEmail === newEmail.toLowerCase()
      ) {
        return state;
      }
      let changed = false;
      const byId: Record<string, Topic> = {};
      for (const id of Object.keys(state.byId)) {
        const existing = state.byId[id];
        const emails = existing.managerEmails;
        if (emails?.some(e => e.trim().toLowerCase() === oldEmail)) {
          changed = true;
          byId[id] = {
            ...existing,
            managerEmails: emails.map(e =>
              e.trim().toLowerCase() === oldEmail ? newEmail : e
            ),
          };
        } else {
          byId[id] = existing;
        }
      }
      // Return the same reference when no topic referenced the old email, so
      // consumers don't re-render needlessly.
      return changed ? { ...state, byId } : state;
    }
    case 'UPDATE_CUSTOM_TEMPLATES': {
      const existing = state.byId[topic.topicId];
      if (!existing) {
        return state;
      }
      return {
        ...state,
        byId: {
          ...state.byId,
          [topic.topicId]: {
            ...existing,
            customTemplates: topic.customTemplates,
          },
        },
      };
    }
    default:
      return state;
  }
}

export function selectAllTopics(state: TopicState): Topic[] {
  return [...state.allIds.map(id => state.byId[id])].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

export function selectTopic(state: TopicState, id: string): Topic | undefined {
  return state.byId[id];
}

export function selectTopicMutations(
  state: TopicState,
  id: string
): TopicMutation[] {
  return state.mutationsById[id] ?? [];
}
